import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import { type Device } from 'types/device.types';

interface Props {
  open: boolean;
  devices: Device[];
  syncing: boolean;
  onClose: () => void;
  onSync: (deviceId: string | undefined) => void;
}

export const SyncOperatorsModal = ({
  open,
  devices,
  syncing,
  onClose,
  onSync,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [targetSpecific, setTargetSpecific] = useState(false);
  const [device, setDevice] = useState<Device | null>(null);

  const handleClose = () => {
    if (syncing) return;
    setTargetSpecific(false);
    setDevice(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (th) => alpha(th.palette.primary.main, 0.15),
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 700,
          pb: 1,
        }}
      >
        {t('operator_importer_sync_title')}
        <IconButton onClick={handleClose} size="small" disabled={syncing}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {t('operator_importer_sync_body')}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={targetSpecific}
              onChange={(e) => {
                setTargetSpecific(e.target.checked);
                if (!e.target.checked) setDevice(null);
              }}
              disabled={syncing}
              size="small"
            />
          }
          label={
            <Typography variant="body2">
              {t('operator_importer_sync_target_specific')}
            </Typography>
          }
        />
        {targetSpecific ? (
          <Autocomplete
            options={devices}
            getOptionLabel={(d) =>
              d.deviceName || d.assetBumperNumber || d.deviceId
            }
            isOptionEqualToValue={(a, b) => a.deviceId === b.deviceId}
            value={device}
            onChange={(_, val) => {
              setDevice(val);
            }}
            disabled={syncing}
            size="small"
            sx={{ mt: 1.5 }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label={t('operator_importer_sync_device_label')}
              />
            )}
          />
        ) : (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', mt: 1, display: 'block' }}
          >
            {t('operator_importer_sync_all_hint')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={syncing}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('operator_importer_cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onSync(targetSpecific ? device?.deviceId : undefined);
          }}
          disabled={syncing || (targetSpecific && !device)}
          startIcon={
            syncing ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <SyncOutlinedIcon />
            )
          }
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('operator_importer_btn_sync_devices')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
